import {
  Button,
  Checkbox,
  Combobox,
  Loader,
  Modal,
  Pill,
  PillsInput,
  Radio,
  Text,
  useCombobox
} from "@marketplace/ui";
import {
  DatePickerInput,
  type DatesRangeValue,
  type DateValue
} from "@marketplace/ui/dates";
import { getRouteApi } from "@tanstack/react-router";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdCheckCircle, MdDateRange } from "react-icons/md";

import { ExportDoneIllustration } from "@/assets/images/export-done-illustration";

import { useExportOrderForm } from "../../hooks/forms/use-export-order-form";

const route = getRouteApi("/orders/");

const PRODUCT_CATEGORIES = [
  { value: "api", label: "API" },
  { value: "software", label: "Software" },
  { value: "connectivity", label: "Connectivity" }
] as const;

const ORDER_STATUSES = [
  { value: "order_created", labelKey: "orderCreated" },
  { value: "waiting_for_payment", labelKey: "waitingForPayment" },
  { value: "paid", labelKey: "paid" },
  { value: "done", labelKey: "done" },
  { value: "failed", labelKey: "failed" }
] as const;

export const ExportOrderModal = () => {
  const { q, start_date, end_date } = route.useSearch();
  const { t } = useTranslation("order", {
    keyPrefix: "orderListPage.exportModal"
  });
  const { t: tStatus } = useTranslation("order", {
    keyPrefix: "orderListPage.statusTab"
  });

  const {
    formMethods: {
      control,
      watch,
      setValue,
      formState: { errors }
    },
    productOptions,
    exportState,
    isModalVisible,
    isProductFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    productSearch,
    setProductSearch,
    handleClose,
    handleDownloadClick
  } = useExportOrderForm({ q, start_date, end_date });

  const watchedCategories = watch("productTypes");
  const watchedStatuses = watch("orderStatuses");
  const watchedProductIds = watch("productIds");

  const [localProductIds, setLocalProductIds] = useState<string[]>(
    watchedProductIds ?? []
  );

  useEffect(() => {
    const formValue = watchedProductIds ?? [];
    const differs =
      localProductIds.length !== formValue.length ||
      !localProductIds.every((v, i) => v === formValue[i]);
    if (differs) setLocalProductIds(formValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedProductIds]);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setProductSearch("");
    }
  });

  const handleProductToggle = (val: string) => {
    setProductSearch("");
    setLocalProductIds((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const handleProductScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const scrollable = target.scrollHeight - target.clientHeight;
      const threshold = Math.min(40, scrollable);
      const nearBottom = scrollable - target.scrollTop < threshold;
      if (nearBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const [selectedProductLabels, setSelectedProductLabels] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    setSelectedProductLabels((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (!localProductIds.includes(id)) delete next[id];
      });
      localProductIds.forEach((id) => {
        const option = productOptions.find((o) => o.value === id);
        if (option) next[id] = option.label;
      });
      return next;
    });
  }, [localProductIds, productOptions]);

  const allCategories = watchedCategories.length === PRODUCT_CATEGORIES.length;
  const allStatuses = watchedStatuses.length === ORDER_STATUSES.length;

  const handleAllCategories = (current: string[]) => {
    if (current.length === PRODUCT_CATEGORIES.length) return [];
    return PRODUCT_CATEGORIES.map((c) => c.value);
  };

  const handleAllStatuses = (current: string[]) => {
    if (current.length === ORDER_STATUSES.length) return [];
    return ORDER_STATUSES.map((s) => s.value);
  };

  const toggleCategory = (value: string, current: string[]) => {
    if (current.includes(value)) {
      return current.filter((v) => v !== value);
    }
    return [...current, value];
  };

  const toggleStatus = (value: string, current: string[]) => {
    if (current.includes(value)) {
      return current.filter((v) => v !== value);
    }
    return [...current, value];
  };

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    start_date ? new Date(start_date) : null,
    end_date ? new Date(end_date) : null
  ]);

  useEffect(() => {
    const from = start_date ? new Date(start_date) : null;
    const to = end_date ? new Date(end_date) : null;
    const fromValid = from instanceof Date && !isNaN(from.getTime());
    const toValid = to instanceof Date && !isNaN(to.getTime());

    if (fromValid && toValid) {
      setDateRange([from, to]);
    } else {
      setDateRange([null, null]);
    }
  }, [start_date, end_date]);

  const handleDateChange = (value: DateValue | DatesRangeValue | Date[]) => {
    let range: [Date | null, Date | null] = [null, null];

    if (Array.isArray(value)) {
      range = [
        value[0] instanceof Date
          ? value[0]
          : value[0]
            ? new Date(value[0] as string)
            : null,
        value[1] instanceof Date
          ? value[1]
          : value[1]
            ? new Date(value[1] as string)
            : null
      ];
    } else if (
      value &&
      typeof value === "object" &&
      "from" in value &&
      "to" in value
    ) {
      range = [
        value.from instanceof Date
          ? value.from
          : value.from
            ? new Date(value.from as string)
            : null,
        value.to instanceof Date
          ? value.to
          : value.to
            ? new Date(value.to as string)
            : null
      ];
    }

    setDateRange(range);
    setValue("start_date", range[0] ? format(range[0], "yyyy-MM-dd") : "");
    setValue("end_date", range[1] ? format(range[1], "yyyy-MM-dd") : "");
  };
  const wrappedHandleClose = () => {
    handleClose();
    if (!start_date || !end_date) {
      setDateRange([null, null]);
    }
  };

  return (
    <Modal
      opened={isModalVisible}
      onClose={wrappedHandleClose}
      closeOnClickOutside={exportState === "form"}
      closeOnEscape={exportState === "form"}
      withCloseButton={exportState !== "processing"}
      size={540}
      title={
        <Text type="heading" className="text-fjord-grey-500">
          {t("title")}
        </Text>
      }
    >
      {exportState === "form" && (
        <div className="space-y-6">
          <div className="space-y-3">
            <Text
              type="heading"
              size="xxs"
              weight="bold"
              className="text-fjord-grey-500"
            >
              {t("date")}
            </Text>
            <DatePickerInput
              type="range"
              rightSection={<MdDateRange />}
              locale="id"
              valueFormat="DD MMM YYYY"
              placeholder={t("selectDate")}
              value={dateRange as any}
              onChange={handleDateChange}
              clearable
              size="sm"
              pickerSize="sm"
            />
          </div>
          <div className="space-y-3">
            <Text
              type="heading"
              size="xxs"
              weight="bold"
              className="text-fjord-grey-500"
            >
              {t("productCategory")}
            </Text>
            <Controller
              name="productTypes"
              control={control}
              render={({ field }) => (
                <div className=" flex items-center">
                  <Checkbox
                    label={t("allCategories")}
                    checked={allCategories}
                    onChange={() =>
                      field.onChange(handleAllCategories(field.value))
                    }
                  />
                  {PRODUCT_CATEGORIES.map((cat) => (
                    <Checkbox
                      key={cat.value}
                      label={cat.label}
                      checked={field.value.includes(cat.value)}
                      onChange={() =>
                        field.onChange(toggleCategory(cat.value, field.value))
                      }
                    />
                  ))}
                </div>
              )}
            />
            {errors.productTypes && (
              <Text className="text-red-500 text-sm">
                {errors.productTypes.message}
              </Text>
            )}
          </div>

          <div className="space-y-3">
            <Text
              type="heading"
              size="xxs"
              weight="bold"
              className="text-fjord-grey-500"
            >
              {t("productName")}
            </Text>
            <Controller
              name="productIds"
              control={control}
              render={({ field }) => (
                <Combobox
                  store={combobox}
                  classNames={{
                    dropdown: "p-0 min-w-[300px]"
                  }}
                >
                  <Combobox.Target>
                    <PillsInput
                      pointer
                      onClick={() => combobox.toggleDropdown()}
                      rightSection={<Combobox.Chevron />}
                      rightSectionPointerEvents="none"
                      size="sm"
                      classNames={{
                        input: "!border-mischka-400 !rounded-lg"
                      }}
                    >
                      <Pill.Group
                        classNames={{
                          group: "max-h-[95px] overflow-y-auto"
                        }}
                      >
                        {localProductIds.length === 0 ? (
                          <PillsInput.Field
                            placeholder={t("productNamePlaceholder")}
                            value={productSearch}
                            onChange={(event) => {
                              setProductSearch(event.currentTarget.value);
                              combobox.openDropdown();
                            }}
                          />
                        ) : (
                          <>
                            {localProductIds.map((id) => (
                              <Pill
                                key={id}
                                withRemoveButton
                                onRemove={() => {
                                  const newValues = localProductIds.filter(
                                    (v) => v !== id
                                  );
                                  setLocalProductIds(newValues);
                                  field.onChange(newValues);
                                }}
                              >
                                {selectedProductLabels[id] ?? id}
                              </Pill>
                            ))}
                            <PillsInput.Field
                              value={productSearch}
                              onChange={(event) => {
                                setProductSearch(event.currentTarget.value);
                                combobox.openDropdown();
                              }}
                            />
                          </>
                        )}
                      </Pill.Group>
                    </PillsInput>
                  </Combobox.Target>

                  <Combobox.Dropdown
                    classNames={{
                      dropdown: "rounded-lg"
                    }}
                  >
                    <Combobox.Options>
                      {isProductFetching ? (
                        <div className="flex justify-center py-6">
                          <Loader size="sm" />
                        </div>
                      ) : productOptions.length === 0 ? (
                        <div className="flex justify-center py-6">
                          <Text className="text-fjord-grey-400 text-sm">
                            {t("noProductsFound")}
                          </Text>
                        </div>
                      ) : (
                        <div
                          className="max-h-[180px] overflow-y-auto"
                          onScroll={handleProductScroll}
                        >
                          {productOptions.map((option) => (
                            <Checkbox
                              key={option.value}
                              label={option.label}
                              checked={localProductIds.includes(option.value)}
                              onChange={() => handleProductToggle(option.value)}
                              classNames={{
                                root: "p-4",
                                label: "text-sm cursor-pointer",
                                labelWrapper: "w-full"
                              }}
                            />
                          ))}
                          {isFetchingNextPage && (
                            <div className="flex justify-center py-4">
                              <Loader size="sm" />
                            </div>
                          )}
                          {!hasNextPage && (
                            <div className="flex justify-center py-4">
                              <Text className="text-fjord-grey-400 text-xs">
                                {t("allProductsLoaded")}
                              </Text>
                            </div>
                          )}
                        </div>
                      )}
                      <hr className="h-px border-0 bg-mischka-100" />
                      <div className="flex justify-end gap-4 p-4">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="w-[128px]"
                          onClick={() => {
                            setLocalProductIds(field.value ?? []);
                            combobox.closeDropdown();
                          }}
                        >
                          {t("cancel")}
                        </Button>
                        <Button
                          size="sm"
                          variant="primary"
                          className="w-[128px]"
                          onClick={() => {
                            field.onChange(localProductIds);
                            combobox.closeDropdown();
                          }}
                        >
                          {t("choose")}
                        </Button>
                      </div>
                    </Combobox.Options>
                  </Combobox.Dropdown>
                </Combobox>
              )}
            />
            {errors.productIds && (
              <Text className="text-red-500 text-sm">
                {errors.productIds.message}
              </Text>
            )}
          </div>

          <div className="space-y-3">
            <Text
              type="heading"
              size="xxs"
              weight="bold"
              className="text-fjord-grey-500"
            >
              {t("orderStatus")}
            </Text>
            <Controller
              name="orderStatuses"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-2 gap-2">
                  <Checkbox
                    label={t("allStatuses")}
                    checked={allStatuses}
                    onChange={() =>
                      field.onChange(handleAllStatuses(field.value))
                    }
                  />
                  {ORDER_STATUSES.map((s) => (
                    <Checkbox
                      key={s.value}
                      label={tStatus(s.labelKey)}
                      checked={field.value.includes(s.value)}
                      onChange={() =>
                        field.onChange(toggleStatus(s.value, field.value))
                      }
                    />
                  ))}
                </div>
              )}
            />
            {errors.orderStatuses && (
              <Text className="text-red-500 text-sm">
                {errors.orderStatuses.message}
              </Text>
            )}
          </div>

          <div className="space-y-3">
            <Text
              type="heading"
              size="xxs"
              weight="bold"
              className="text-fjord-grey-500"
            >
              {t("exportFormat")}
            </Text>
            <Controller
              name="exportFormat"
              control={control}
              render={({ field }) => (
                <Radio.Group value={field.value} onChange={field.onChange}>
                  <div className="flex gap-12">
                    <Radio value="csv" label={t("csv")} />
                    <Radio value="xlsx" label={t("xlsx")} />
                  </div>
                </Radio.Group>
              )}
            />
          </div>

          <div className="space-y-3">
            <Text
              type="heading"
              size="xxs"
              weight="bold"
              className="text-fjord-grey-500"
            >
              {t("sendTo")}
            </Text>
            <Controller
              name="doneSendTo"
              control={control}
              render={({ field }) => (
                <Radio.Group value={field.value} onChange={field.onChange}>
                  <div className="flex gap-8">
                    <Radio value="email" label={t("email")} />
                    <Radio value="device" label={t("device")} />
                  </div>
                </Radio.Group>
              )}
            />
          </div>

          <div className="flex justify-center gap-3 pt-2 ">
            <Button
              variant="secondary"
              onClick={wrappedHandleClose}
              className="w-[140px]"
            >
              {t("cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleDownloadClick}
              className="w-[140px]"
            >
              {t("download")}
            </Button>
          </div>
        </div>
      )}

      {exportState === "processing" && (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <Loader size={256} color="#85C9FF" />
          <Text type="heading" size="s" className="text-fjord-grey-500">
            {t("processing")}
          </Text>
          <Text className="text-fjord-grey-400">{t("processingMessage")}</Text>
        </div>
      )}

      {exportState === "done" && (
        <>
          <div className="relative mx-auto">
            <ExportDoneIllustration className="absolute inset-x-[150px]" />
          </div>
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <MdCheckCircle
              className="text-green-500 relative z-10"
              size={100}
            />
            <p className="text-fjord-grey-500 font-semibold text-center z-10">
              {t("done")}{" "}
              <span className="text-fjord-grey-400">{t("doneMessage")}</span>
            </p>
          </div>
        </>
      )}
    </Modal>
  );
};
